import { useCallback, useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/client';

import { AssetClass, FileType, ProductType, QmMode } from '__generated__/globalTypes';
import {
  GetCollateralFileUploadUrl as getCollateralFileUploadURL,
  GetCollateralFileUploadUrlVariables as getCollateralFileUploadURLVariables,
} from 'mutation/__generated__/getCollateralFileUploadURL';
import {
  GetContractPostUrl,
  GetContractPostUrlVariables,
} from 'mutation/__generated__/getContractPostUrls';
import { GetFilePostUrls, GetFilePostUrlsVariables } from 'mutation/__generated__/getFilePostUrls';
import GET_COLLATERAL_FILE_UPLOAD_URL_MUTATION from 'mutation/getCollateralFileUploadURL';
import GET_CONTRACT_FILE_POST_URL_MUTATION from 'mutation/getContractPostUrls';
import GET_FILE_POST_URLS_MUTATION from 'mutation/getFilePostUrls';

import useFileUuid from './useFileUuid';
import {
  GetWireDetailsFileUploadUrl as GetWireDetailsFileUploadURL,
  GetWireDetailsFileUploadUrlVariables as GetWireDetailsFileUploadURLVariables,
} from '../../mutation/__generated__/getWireDetailsUploadUrls';
import GET_WIRE_DETAILS_UPLOAD_URL from '../../mutation/getWireDetailsUploadUrls';

// Map MIME types to file extensions for fallback validation
const MIME_TO_EXTENSIONS: Record<string, string[]> = {
  'application/zip': ['.zip'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': ['.xlsb'],
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
  'text/comma-separated-values': ['.csv'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tif', '.tiff'],
};

// Validate file by extension when MIME type detection fails
const isValidFileByExtension = (fileName: string, allowedMimeTypes: string[]): boolean => {
  const fileExt = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return allowedMimeTypes.some((mimeType) =>
    MIME_TO_EXTENSIONS[mimeType]?.some((ext) => fileExt === ext),
  );
};

type UseS3UploaderProps = {
  onSuccess?: (file: File, versionId: string, uuid?: string) => void;
  onProgress?: (progress: number, file?: File) => void;
  onError?: (error: unknown) => void;
  fileType: FileType;
  parentId?: string;
  assetClass?: AssetClass;
  productType?: ProductType;
  qmMode?: QmMode;
  allowedMimeTypes?: string[];
  isContract?: boolean;
  isCollateral?: boolean;
  isWire?: boolean;
  uploadedBy?: string;
  loanId?: string;
};

const usePreSignedUrlQuery = (
  fileType: FileType,
  parentId?: string,
  assetClass?: AssetClass,
  productType?: ProductType,
  isContract?: boolean,
  isCollateral?: boolean,
  isWire?: boolean,
  qmMode?: QmMode,
) => {
  const client = useApolloClient();

  const getPresignedUrl = async (fileName: string, contentType: string, loanId: string) => {
    if (isContract && parentId != null) {
      const query: GetContractPostUrlVariables = {
        input: {
          dealId: parentId,
          fileName,
          contentType,
        },
      };

      const result = await client.mutate<GetContractPostUrl, GetContractPostUrlVariables>({
        mutation: GET_CONTRACT_FILE_POST_URL_MUTATION,
        variables: query,
      });

      if (!result.data) {
        throw new Error('Could not obtain a pre-signed upload URL for contract.');
      }
      return result.data.getContractFilePostUrl;
    } else if (isWire && parentId) {
      const query: GetWireDetailsFileUploadURLVariables = {
        input: {
          dealID: parentId,
          fileName,
        },
      };

      const result = await client.mutate<
        GetWireDetailsFileUploadURL,
        GetWireDetailsFileUploadURLVariables
      >({
        mutation: GET_WIRE_DETAILS_UPLOAD_URL,
        variables: query,
      });

      if (!result.data) {
        throw new Error('Failed to get presigned URL for wire details upload');
      }

      return {
        url: result.data.getWireDetailsFileUploadURL.URL,
        metadata: (result.data.getWireDetailsFileUploadURL as any).Metadata,
      };
    } else if (isCollateral) {
      const query: getCollateralFileUploadURLVariables = {
        input: {
          dealID: parentId || '',
          fileName,
          loanID: loanId || '',
        },
      };

      const result = await client.mutate<
        getCollateralFileUploadURL,
        getCollateralFileUploadURLVariables
      >({
        mutation: GET_COLLATERAL_FILE_UPLOAD_URL_MUTATION,
        variables: query,
      });

      if (!result.data) {
        throw new Error('Could not obtain a pre-signed upload URL for collateral zip.');
      }

      return {
        url: result.data.getCollateralFileUploadURL.URL,
        metadata: result.data.getCollateralFileUploadURL.Metadata || {},
      };
    } else {
      // Default S3 uploader logic
      const query = {
        mutation: GET_FILE_POST_URLS_MUTATION,
        variables: {
          parentId,
          fileType,
          fileName,
          contentType,
          assetClass,
          productType,
          qmMode,
        },
      };

      const result = await client.mutate<GetFilePostUrls, GetFilePostUrlsVariables>(query);
      if (!result.data) {
        throw new Error('Could not obtain a pre-signed upload URL.');
      }
      return result.data.getFilePostUrl;
    }
  };

  return {
    getPresignedUrl,
  };
};

export const useS3Uploader = ({
  onSuccess,
  onProgress,
  onError,
  fileType,
  parentId,
  allowedMimeTypes,
  isContract,
  isCollateral,
  uploadedBy,
  loanId,
  isWire,
  ...rest
}: UseS3UploaderProps) => {
  const { fetchFileUuid } = useFileUuid();
  const { getPresignedUrl } = usePreSignedUrlQuery(
    fileType,
    parentId,
    rest.assetClass,
    rest.productType,
    isContract,
    isCollateral,
    isWire,
    rest.qmMode,
  );
  const getPresignedUrlRef = useRef<
    (file: File) => Promise<string | { url: string; metadata: Object }>
  >(async () => '');
  useEffect(() => {
    getPresignedUrlRef.current = async (file: File) => {
      return getPresignedUrl(file.name, file.type, loanId || '');
    };
  }, [getPresignedUrl, loanId]);

  const upload = useCallback(
    async (file: File) => {
      // Validate file type: check MIME type first, fallback to extension
      if (allowedMimeTypes != null) {
        const hasValidMimeType = allowedMimeTypes.includes(file.type);
        const hasValidExtension = isValidFileByExtension(file.name, allowedMimeTypes);

        if (!hasValidMimeType && !hasValidExtension) {
          const error = new Error('Invalid file type. Only CSV, TSV, and Excel files are allowed.');
          console.error(error.message);
          onError?.(error);
          throw error;
        }
      }

      try {
        const presignedUrlResult = await getPresignedUrlRef.current(file);

        // Handle different return types from getPresignedUrl
        let presignedUrl: string;
        let metadata: { [key: string]: string } | undefined;

        // Add server-provided metadata if provided
        if (typeof presignedUrlResult === 'object' && presignedUrlResult !== null) {
          // { url, metadata }
          presignedUrl = (presignedUrlResult as any).url;
          metadata = (presignedUrlResult as any).metadata;
        } else {
          presignedUrl = presignedUrlResult as string;
        }

        const versionId = await performUpload(
          file,
          presignedUrl,
          onProgress,
          isContract,
          isCollateral,
          metadata,
        );

        if (!isCollateral) {
          try {
            let uuid;
            if ('assetClass' in rest) {
              uuid = await fetchFileUuid(
                file,
                fileType,
                versionId,
                parentId,
                rest.assetClass,
                rest.productType,
              );
            } else {
              uuid = await fetchFileUuid(file, fileType, versionId, parentId);
            }
            onSuccess?.(file, versionId || '', uuid);
          } catch (error) {
            console.log('Failed to fetch file UUID:', error);
            onSuccess?.(file, versionId || '');
          }
        } else {
          onSuccess?.(file, versionId || '');
        }

        return versionId;
      } catch (error) {
        console.error('Failed to upload file:', error);
        onError?.(error);
        throw error;
      }
    },
    [
      onSuccess,
      onProgress,
      onError,
      getPresignedUrlRef,
      fileType,
      parentId,
      rest,
      fetchFileUuid,
      allowedMimeTypes,
      isContract,
      isCollateral,
      uploadedBy,
      loanId,
    ],
  );

  return {
    upload,
  };
};

export const performUpload = (
  file: File,
  presignedUrl: string,
  onProgress?: (progress: number, file?: File) => void,
  isContract?: boolean,
  isCollateral?: boolean,
  metadata?: { [key: string]: string },
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);

    // Add server-provided metadata with x-amz-meta- prefix
    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        xhr.setRequestHeader(`x-amz-meta-${key}`, value);
      });
    }

    xhr.onerror = (err) => {
      console.log(err, 'error in upload');
      console.log(`Upload failed with status ${xhr.status}: ${xhr.statusText} : ${xhr}`);
      reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
    };
    xhr.onabort = () => reject(new Error('Upload aborted.'));
    xhr.onload = () => {
      if (xhr.status === 200) {
        const versionId = xhr.getResponseHeader('x-amz-version-id');
        if (isContract || isCollateral || versionId !== null) {
          resolve(versionId || '');
        } else {
          reject(new Error('Upload failed - no version ID returned.'));
        }
      } else {
        console.log(xhr, 'xhr in upload');
        console.log(`Upload failed with status ${xhr.status}: ${xhr.statusText}`);
        reject(new Error(`Upload failed with status ${xhr.status}.`));
      }
    };

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress?.(percentComplete, file);
      }
    };

    xhr.send(file);
  });
};
